import React, { SyntheticEvent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BOUserStatusEnum,
  FullBOUserResponseModel,
  MemberAdditionalInfoReplyRequestModel,
} from 'api/backofficeApiClient';
import {
  hideMemberPulsatingIndicator,
  sendMemberAdditionalInfoResponse,
} from 'redux/reducers/accountSlice';
import {
  ProfileStatus,
  DEFAULT_ACTION_BTN_TEXT,
  getProfileProgress,
  AdditionalInfoResponseModel,
} from 'utils/profileProgressUtils/generalProgressUtils';
import { CompanyProfileSection } from '../enums';
import BaseAlert from 'components/wrappers/BaseAlert';
import { MemberProfileSectionsEnum } from 'utils/accountUtils';
import {
  BaseProgressStep,
  BaseProgressPercentage,
  BaseProgressTrackerWrapper,
} from 'components/wrappers/BaseProgressBar';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { unfilledMyProfileSectionsSelector } from 'redux/selectors/accountSelector';
import { getMemberProgressStages } from 'utils/profileProgressUtils/memberProgressUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './MemberProgressBar.module.less';

type Props = {
  member: FullBOUserResponseModel;
  onChange: (recruiter: FullBOUserResponseModel) => void;
};

const progressStepErrorText = 'Waiting for admin response';

export default function MemberProgressBar(props: Props) {
  const { member, onChange } = props;
  const { stages, userId } = member;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const navigate = useNavigate();
  const unfilledSections = useAppSelector(unfilledMyProfileSectionsSelector);

  const handleSectionClick = (
    event: SyntheticEvent,
    section: MemberProfileSectionsEnum,
  ) => {
    event.preventDefault();

    switch (section) {
      case MemberProfileSectionsEnum.BillingInformation:
        navigate(
          `/company-profile?section=${CompanyProfileSection.AdditionalInfo}`,
          {
            replace: true,
            state: {
              withScroll: true,
            },
          },
        );
        return;

      case MemberProfileSectionsEnum.BankingInformation:
      case MemberProfileSectionsEnum.EntityInformation:
      case MemberProfileSectionsEnum.PricingAgreement:
        navigate(
          `/company-profile?section=${CompanyProfileSection.PrimaryInfo}`,
          {
            replace: true,
            state: {
              withScroll: true,
            },
          },
        );
        return;

      default:
        return;
    }
  };

  const joinedSections = useMemo(
    () =>
      unfilledSections.length
        ? (unfilledSections
            ?.map<React.ReactNode>((section) => (
              <a
                href="/"
                key={`uncompletted-section-${section}`}
                onClick={(event) => handleSectionClick(event, section)}
              >
                {section}
              </a>
            ))
            .reduce((previous, current) => [
              previous,
              ', ',
              current,
            ]) as React.ReactNode[])
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unfilledSections],
  );

  const memberStages = useMemo(() => {
    const stagesOptions = {
      isAdmin,
      stages,
      isRequest: false,
      unfilledSections: joinedSections,
    };

    return getMemberProgressStages(stagesOptions);
  }, [stages, isAdmin, joinedSections]);

  const handleResponseSent = useCallback(
    async (responseInfo: AdditionalInfoResponseModel) => {
      const { responseMessage, stage } = responseInfo;

      const data: MemberAdditionalInfoReplyRequestModel = {
        additionalInfoReply: responseMessage,
        currentStage: stage as BOUserStatusEnum,
      };

      const result = await dispatch(sendMemberAdditionalInfoResponse(data));

      if (sendMemberAdditionalInfoResponse.fulfilled.match(result)) {
        BaseAlert.success('Response successfully sent.');
        onChange(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [dispatch, onChange],
  );

  const onPulsatingHover = useCallback(
    async (stage: ProfileStatus) => {
      const result = await dispatch(
        hideMemberPulsatingIndicator({
          userId,
          currentStage: stage as BOUserStatusEnum,
        }),
      );

      if (hideMemberPulsatingIndicator.fulfilled.match(result)) {
        onChange(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [dispatch, onChange, userId],
  );

  return (
    <div className={styles.progressTracker}>
      <div className={styles.progressPercentage}>
        <BaseProgressPercentage percent={getProfileProgress(memberStages)} />
      </div>

      <div className="page-box">
        <div className="page-inner-container">
          <BaseProgressTrackerWrapper>
            {memberStages.map((progressStep, index) => (
              <BaseProgressStep
                key={index}
                index={index}
                {...progressStep}
                displayRequestButton={false}
                errorText={progressStep.errorText || progressStepErrorText}
                actionBtnText={
                  progressStep.actionBtnText || DEFAULT_ACTION_BTN_TEXT
                }
                onResponseSent={handleResponseSent}
                onPulsatingHover={onPulsatingHover}
              />
            ))}
          </BaseProgressTrackerWrapper>
        </div>
      </div>
    </div>
  );
}
